import React, { useState, useCallback, useEffect } from "react";
import SelectAsync from 'react-select/async';
import { apiRequest } from "../../../hooks/apiRequest";
import debounce from 'lodash/debounce';
import dayjs from "dayjs";
import MotivoSelect from "./Components/MotivoSelect";
import SignosMedica from "./Components/SignosMedica";
import SignosPsicologia from "./Components/SignosPsicologia";
import DocumentoItem from "./Components/DocumentoItem";
import Insumos from "./Components/Insumos";

const Form = ({ visita, setVisita, setEsValido, errors, saving, original }) => {
    const onChangeProperty = (property, value) => {
        setVisita({
            ...visita,
            [property]: value
        });
    };

    const getContactoOptions = async (search) => {
        return (await apiRequest({}, `salud/contactos?buscar=${search}`, 'get')).data;
    };

    const debouncedLoadOptions = useCallback(
        debounce((inputValue, callback) => {
            getContactoOptions(inputValue).then(data => callback(data));
        }, 500) // 500 ms de espera después de que el usuario deja de escribir
        , []
    );

    const setDocumentos = (values) => {
        if (typeof values === 'function') {
            setVisita({...visita, documentos: values(visita.documentos ?? [])});
        } else {
            setVisita({...visita, documentos: values});
        }
    };

    const addDocumento = () => {
        setDocumentos(prev => ([
            ...prev,
            {}
        ]))
    };

    useEffect(() => {
        if (!visita.paciente || !visita.fecha || !visita.tipo || !visita.signos || !visita.motivo || !visita.diagnostico) {
            setEsValido(false);

            return;
        }

        for (const doc of visita.documentos ?? []) {
            if (doc.borrar === 1) continue;

            if (!doc.nombre || (!doc.archivo && !doc.id)) {
                setEsValido(false);
                return;
            }
        }
        
        setEsValido(true);
    }, [visita]);

    useEffect(() => {
        setVisita({
            ...visita,
            motivo: original?.tipo === visita.tipo ? visita.motivo : null,
            signos: original?.tipo === visita.tipo ? visita.signos : null,
            derivacion: visita.derivacion ? 1 : 0,
        });
    }, [visita.tipo]);

    return <>
        <div className="row mt-4">
            <div className="col-md-12">
                <div className="form-group">
                    <label>Contacto *</label>
                    <SelectAsync
                        name="paciente"
                        isClearable
                        cacheOptions
                        placeholder="Seleccionar..."
                        defaultOptions
                        onChange={e => onChangeProperty('paciente', e ?? null)}
                        loadOptions={debouncedLoadOptions}
                        value={visita.paciente}
                        isDisabled={saving || visita.id}
                    />
                    {(errors.paciente || errors['paciente.pacienteable_id'] || errors['paciente.pacienteable_type'] || errors['paciente.id']) && (
                        <small className='help-blockParams form-Text'>
                            Este campo es requerido.
                        </small>
                    )}
                </div>
            </div>

            <div className="col-md-12">
                <div className="form-group">
                    <label>Fecha y Hora *</label>
                    <input 
                        type="datetime-local"
                        value={visita.fecha ?? ''}
                        onChange={e => onChangeProperty('fecha', e.target.value)}
                        className="form-control" 
                        name="fecha"
                        disabled={saving}
                    />
                    {errors.fecha && (
                        <small className='help-blockParams form-Text'>
                            {errors.fecha[0]}
                        </small>
                    )}
                </div>
            </div>

            <div className="col-md-12">
                <div className="form-group">
                    <label>Tipo de Visita *</label>
                    <select
                        name="tipo"
                        className="form-control"
                        placeholder="Seleccionar..."
                        onChange={e => onChangeProperty('tipo', e.target.value)}
                        value={visita.tipo ?? ''}
                        disabled={saving}
                    >
                        <option value="Médica">Médica</option>
                        <option value="Psicología">Psicología</option>
                    </select>
                    {errors.tipo && (
                        <small className='help-blockParams form-Text'>
                            {errors.tipo[0]}
                        </small>
                    )}
                </div>
            </div>

            <div className="col-md-12">
                <div className="form-group">
                    <label>Motivo de Visita *</label>

                    <MotivoSelect
                        value={visita.motivo}
                        disabled={!visita.tipo || saving}
                        onChange={e => onChangeProperty('motivo', e ?? null)}
                        tipo={visita.tipo}
                    />

                    {errors.motivo && (
                        <small className='help-blockParams form-Text'>
                            {errors.motivo[0]}
                        </small>
                    )}
                </div>
            </div>

            <div className="col-md-12">
                <div className="form-group">
                    <label>Observaciones</label>

                    <textarea
                        name="observaciones"
                        className="form-control"
                        cols="30"
                        rows="2"
                        onChange={e => onChangeProperty('observaciones', e.target.value)}
                        value={visita.observaciones ?? ''}
                        disabled={saving}
                    ></textarea>

                    {errors.observaciones && (
                        <small className='help-blockParams form-Text'>
                            {errors.observaciones[0]}
                        </small>
                    )}
                </div>
            </div>

            {visita.tipo === 'Médica'
                ? <SignosMedica signos={visita.signos ?? {}} onChange={(e) => onChangeProperty('signos', e)} errors={errors} disabled={saving} />
                : <SignosPsicologia signos={visita.signos ?? {}} onChange={(e) => onChangeProperty('signos', e)} errors={errors} disabled={saving} />
            }

            {/* insertar componente de signos */}

            <div className="col-md-12">
                <div className="form-group">
                    <label>Diagnóstico Preliminar *</label>

                    <textarea
                        name="diagnostico"
                        className="form-control"
                        cols="30"
                        rows="2"
                        onChange={e => onChangeProperty('diagnostico', e.target.value)}
                        value={visita.diagnostico ?? ''}
                        disabled={saving}
                    ></textarea>
                    
                    {errors.diagnostico && (
                        <small className='help-blockParams form-Text'>
                            {errors.diagnostico[0]}
                        </small>
                    )}
                </div>
            </div>

            <div className="col-md-12">
                <Insumos
                    insumos={visita.insumos ?? []}
                    onChange={(e) => onChangeProperty('insumos', e)}
                    errors={errors}
                    disabled={saving}
                ></Insumos>
            </div>

            <div className="col-md-12">
                <div className="form-group">
                    <label>{visita.type === "Médica" ? "Derivación médica externa" : "Derivación a especialista externo" }</label>

                    <label className='switch switch-3d switch-success mr-3'>
                        <input
                            type='checkbox'
                            className='switch-input'
                            id='derivacion'
                            name='derivacion'
                            checked={visita.derivacion == 1}
                            onChange={e => onChangeProperty('derivacion', e.target.checked ? 1 : 0)}
                            disabled={saving}
                        />
                        <span className='switch-label' />
                        <span className='switch-handle' />
                    </label>

                    {errors.derivacion && (
                        <small className='help-blockParams form-Text'>
                            {errors.derivacion[0]}
                        </small>
                    )}
                </div>
            </div>
        </div>

        <div className="row form-group">
            <div className="col col-md-12">
                <h3>Documentos Adjuntos</h3>
            </div>
        </div>
        <div>
            {(visita.documentos ?? []).filter(doc => doc.borrar !== 1).map((documento, index) => (
                <DocumentoItem key={index}
                    documento={documento}
                    index={index}
                    setDocumentos={setDocumentos}
                />
            ))}

            <div className="row form-group">
                <div className="col">
                    <button type="button" className="btn float-right" onClick={addDocumento}>
                        + Agregar Documento
                    </button> 
                </div>
            </div>	
        </div>
    </>;
};

export default Form;
